import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getNotifications,
  markAsRead,
  getUnreadCount,
  markAllRead,
} from "../../api/notifications";
import { toast } from "react-toastify";
import { getLang } from "../../store/uiStore";
import "./Notifications.css";

export default function NotificationsPage() {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("all");
  const [unreadCount, setUnreadCount] = useState(0);
  const [knownIds, setKnownIds] = useState(new Set());

  const navigate = useNavigate();
  const lang = getLang();

  const tr = useMemo(
    () => ({
      title: lang === "ru" ? "Уведомления" : "Хабарҳо",
      subtitle:
        lang === "ru"
          ? "Важные события и изменения в системе"
          : "Рӯйдодҳо ва тағйироти муҳими низом",
      all: lang === "ru" ? "Все" : "Ҳама",
      unread: lang === "ru" ? "Непрочитанные" : "Хонда нашуда",
      important: lang === "ru" ? "Важные" : "Муҳим",
      reports: lang === "ru" ? "Отчёты" : "Ҳисоботҳо",
      markAll: lang === "ru" ? "Прочитать все" : "Ҳамаро хондашуда қайд кардан",
      unreadCount: lang === "ru" ? "непрочитанных" : "хонда нашуда",
      emptyAll: lang === "ru" ? "Нет уведомлений" : "Хабаре нест",
      emptyUnread:
        lang === "ru"
          ? "Нет непрочитанных уведомлений"
          : "Хабарҳои хонданашуда нестанд",
      emptyHigh:
        lang === "ru"
          ? "Нет важных уведомлений"
          : "Хабарҳои муҳим нестанд",
      emptyReport:
        lang === "ru"
          ? "Нет уведомлений об отчётах"
          : "Хабарҳо дар бораи ҳисоботҳо нестанд",
      today: lang === "ru" ? "Сегодня" : "Имрӯз",
      yesterday: lang === "ru" ? "Вчера" : "Дирӯз",
      system: lang === "ru" ? "Система" : "Система",
      report: lang === "ru" ? "Отчёт" : "Ҳисобот",
      emergency: lang === "ru" ? "Экстренное извещение" : "Хабарномаи фаврӣ",
      other: lang === "ru" ? "Система" : "Система",
      high: lang === "ru" ? "Высокий приоритет" : "Афзалияти баланд",
      medium: lang === "ru" ? "Средний приоритет" : "Афзалияти миёна",
      normal: lang === "ru" ? "Обычный приоритет" : "Афзалияти муқаррарӣ",
      new: lang === "ru" ? "Новое" : "Нав",
    }),
    [lang]
  );

  useEffect(() => {
    // Existing initial loading behavior.
    load();
    loadCount();

    // Existing known-notification initialization for toast.
    getNotifications().then((res) => {
      const ids = new Set(res.data.map((n) => n.id));
      setKnownIds(ids);
    });

    // Existing 30-second polling interval.
    const interval = setInterval(() => {
      checkNewNotifications();
    }, 30000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkNewNotifications = () => {
    getNotifications()
      .then((res) => {
        const latest = res.data.slice(0, 5);

        const newOnes = latest.filter(
          (item) => !knownIds.has(item.id) && !item.is_read
        );

        const getToastType = (priority) => {
          if (priority === "high") return "error";
          if (priority === "medium") return "warning";
          return "info";
        };

        newOnes.forEach((item) => {
          toast[getToastType(item.priority)](
            `${item.title}\n${item.message}`,
            {
              onClick: () => {
                if (item.url) navigate(item.url);
              },
            }
          );
        });

        const updatedIds = new Set(knownIds);
        latest.forEach((item) => updatedIds.add(item.id));

        setKnownIds(updatedIds);
        loadCount();
      })
      .catch(() => {});
  };

  const load = () => {
    getNotifications()
      .then((res) => setList(res.data))
      .catch(() => setList([]));
  };

  const loadCount = () => {
    getUnreadCount()
      .then((res) => setUnreadCount(res.data.count))
      .catch(() => setUnreadCount(0));
  };

  const handleClick = (item) => {
    if (!item.is_read) {
      markAsRead(item.id)
        .then(() => {
          load();
          loadCount();
        })
        .catch(() => {});
    }

    if (item.url) {
      navigate(item.url);
    }
  };

  const filtered = list.filter((item) => {
    if (filter === "unread") return !item.is_read;
    if (filter === "high") return item.priority === "high";
    if (filter === "report") return item.type === "report";
    return true;
  });

  const counts = {
    all: list.length,
    unread: list.filter((item) => !item.is_read).length,
    high: list.filter((item) => item.priority === "high").length,
    report: list.filter((item) => item.type === "report").length,
  };

  const getTypeIcon = (type) => {
    if (type === "report") return "▤";
    if (type === "emergency") return "!";
    return "i";
  };

  const getTypeLabel = (type) => {
    if (type === "report") return tr.report;
    if (type === "emergency") return tr.emergency;
    return tr.other;
  };

  const getPriorityLabel = (priority) => {
    if (priority === "high") return tr.high;
    if (priority === "medium") return tr.medium;
    return tr.normal;
  };

  const getPriorityClass = (priority) => {
    if (priority === "high") return "is-high";
    if (priority === "medium") return "is-medium";
    return "is-normal";
  };

  const formatDate = (value) => {
    if (!value) return "";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";

    const now = new Date();
    const yesterday = new Date();
    yesterday.setDate(now.getDate() - 1);

    const sameDay = (a, b) =>
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate();

    const time = date.toLocaleTimeString(
      lang === "ru" ? "ru-RU" : "tg-TJ",
      { hour: "2-digit", minute: "2-digit" }
    );

    if (sameDay(date, now)) return `${tr.today}, ${time}`;
    if (sameDay(date, yesterday)) return `${tr.yesterday}, ${time}`;

    return date.toLocaleDateString(
      lang === "ru" ? "ru-RU" : "tg-TJ",
      {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }
    );
  };

  const emptyText = {
    all: tr.emptyAll,
    unread: tr.emptyUnread,
    high: tr.emptyHigh,
    report: tr.emptyReport,
  }[filter];

  return (
    <main className="nizom-notifications-page">
      <section className="nizom-notifications-shell">
        <header className="nizom-notifications-header">
          <div>
            <h1>{tr.title}</h1>
            <p>{tr.subtitle}</p>
          </div>

          {unreadCount > 0 && (
            <div className="nizom-notifications-unread-summary">
              <span>{unreadCount}</span>
              <small>{tr.unreadCount}</small>
            </div>
          )}
        </header>

        <div className="nizom-notifications-toolbar">
          <div
            className="nizom-notifications-tabs"
            role="tablist"
            aria-label={tr.title}
          >
            {[
              ["all", tr.all],
              ["unread", tr.unread],
              ["high", tr.important],
              ["report", tr.reports],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={filter === key}
                className={
                  filter === key
                    ? "nizom-notification-tab active"
                    : "nizom-notification-tab"
                }
                onClick={() => setFilter(key)}
              >
                <span>{label}</span>
                <b>{counts[key]}</b>
              </button>
            ))}
          </div>

          {list.length > 0 && unreadCount > 0 && (
            <button
              type="button"
              className="nizom-notifications-mark-all"
              onClick={() => {
                markAllRead()
                  .then(() => {
                    load();
                    loadCount();
                  })
                  .catch(() => {});
              }}
            >
              <span aria-hidden="true">✓</span>
              {tr.markAll}
            </button>
          )}
        </div>

        <div className="nizom-notifications-list">
          {filtered.length === 0 ? (
            <div className="nizom-notifications-empty">
              <div className="nizom-notifications-empty-icon">✓</div>
              <h3>{emptyText}</h3>
              <p>
                {filter === "all"
                  ? lang === "ru"
                    ? "Новые уведомления появятся здесь."
                    : "Хабарҳои нав дар ин ҷо пайдо мешаванд."
                  : ""}
              </p>
            </div>
          ) : (
            filtered.map((item) => (
              <article
                key={item.id}
                className={`nizom-notification-card ${
                  item.is_read ? "is-read" : "is-unread"
                } ${getPriorityClass(item.priority)}`}
                onClick={() => handleClick(item)}
                role={item.url ? "button" : undefined}
                tabIndex={item.url ? 0 : undefined}
                onKeyDown={(event) => {
                  if (item.url && (event.key === "Enter" || event.key === " ")) {
                    event.preventDefault();
                    handleClick(item);
                  }
                }}
              >
                <div className="nizom-notification-icon" aria-hidden="true">
                  {getTypeIcon(item.type)}
                </div>

                <div className="nizom-notification-main">
                  <div className="nizom-notification-top">
                    <div className="nizom-notification-title-wrap">
                      {!item.is_read && (
                        <span className="nizom-notification-dot" aria-hidden="true" />
                      )}
                      <h3>{item.title}</h3>
                      {!item.is_read && (
                        <span className="nizom-notification-new">{tr.new}</span>
                      )}
                    </div>

                    {item.url && (
                      <span className="nizom-notification-arrow" aria-hidden="true">
                        →
                      </span>
                    )}
                  </div>

                  <p className="nizom-notification-message">{item.message}</p>

                  <div className="nizom-notification-meta">
                    <span>{getTypeLabel(item.type)}</span>
                    <span className="meta-separator">•</span>
                    <span className={`priority-text ${getPriorityClass(item.priority)}`}>
                      {getPriorityLabel(item.priority)}
                    </span>
                    <span className="meta-separator">•</span>
                    <time dateTime={item.created_at || undefined}>
                      {formatDate(item.created_at)}
                    </time>
                  </div>
                </div>
              </article>
            ))
          )}
        </div>
      </section>
    </main>
  );
}
